import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CloseOutlined, DatabaseOutlined, DownloadOutlined, EyeOutlined, FileTextOutlined, GlobalOutlined, LeftOutlined,
  LoadingOutlined, PictureOutlined, PlaySquareOutlined, RightOutlined, RocketOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../../services/api';
import {
  AgentArtifactCategory, AgentArtifactRecord, listAgentArtifacts,
} from '../../services/agentRuntimeApi';
import { getRecentTasks, getFileDownloadUrl, CollabTask } from '../../services/teamCollabApi';
import { useAuthStore } from '../../stores/authStore';

/* ============================================================
   产物记录（历史生成的图片 / PPT / 文档 / 业务智能体成果，一处翻阅，全部分页）
   通用协作任务收录文件产物；业务智能体按智能体分类收录完整成果。
   PPT 支持页内直接打开"PPT 浏览器"逐页翻看。
   ============================================================ */

type Tab = 'image' | 'ppt' | 'doc' | `agent:${string}`;
const PAGE = 12;          // PPT：前端分页
const IMG_PAGE = 10;      // 设计图：与后台分页接口一致（每页 10）
const DOC_PAGE = 12;      // 文档：每页展示条数
const AGENT_PAGE = 12;    // 业务智能体产物：每页展示条数
const DOC_BATCH = 50;     // 文档：后台每批拉原始任务数——⚠️必须=后台 take 上限(controller Math.min(50,take))，
                          // 否则"返回数 < BATCH"会被后台截断误判成"没有更多"，导致只拉第一批就停

interface DesignItem { id: string; status?: string; assetId?: string; resultAssetId?: string; prompt?: string; createdAt?: string }
interface KnowledgeRef { name: string; score?: number; source: 'doc' | 'intel' }
interface PptItem {
  id: string;
  title?: string;
  sourceText?: string;
  requirement?: string;
  kbDocs?: KnowledgeRef[];
  slideCount?: number;
  createdAt?: string;
  updatedAt?: string;
}
interface PptSlide { id?: string; no?: number; title?: string; subtitle?: string; bullets?: string[]; assetId?: string; imageUrl?: string; type?: string }

type ArtifactDetailState =
  | { kind: 'doc'; item: CollabTask }
  | { kind: 'ppt'; item: PptItem };

const TASK_PREVIEW_CHARS = 50;

const cleanTaskText = (text?: string) => String(text || '')
  .replace(/^【[^】]{1,30}】\s*/, '')
  .split(/\n\n---\n# 本次交办引用的附件/)[0]
  .replace(/\s*@组件\[[^\]]+\](?:#[\w-]+)?/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const taskPreview = (text?: string) => {
  const clean = cleanTaskText(text);
  return clean.length > TASK_PREVIEW_CHARS ? `${clean.slice(0, TASK_PREVIEW_CHARS)}…` : clean;
};

const parseKnowledgeRefs = (raw: unknown): KnowledgeRef[] => {
  let source: unknown = raw;
  if (typeof raw === 'string') {
    try { source = JSON.parse(raw); } catch { source = []; }
  }
  if (!Array.isArray(source)) return [];
  const refs = source.map((item: any): KnowledgeRef | null => {
    const name = String(item?.name || item?.title || item?.fileName || '').trim();
    if (!name) return null;
    const isIntel = item?.source === 'intel'
      || /intel|情报/i.test(`${item?.source || ''} ${item?.category || ''} ${name}`);
    return {
      name,
      score: typeof item?.score === 'number' ? item.score : undefined,
      source: isIntel ? 'intel' : 'doc',
    };
  }).filter((item): item is KnowledgeRef => Boolean(item));
  const deduped = new Map<string, KnowledgeRef>();
  for (const ref of refs) deduped.set(`${ref.source}:${ref.name}`, ref);
  return Array.from(deduped.values());
};

const fmtTime = (ts?: string) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('zh-CN', { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** 简易分页条 */
const Pager: React.FC<{ page: number; total: number; onPage: (p: number) => void; size?: number }> = ({ page, total, onPage, size = PAGE }) => {
  const pages = Math.max(1, Math.ceil(total / size));
  if (pages <= 1) return null;
  return (
    <div className="pager">
      <button disabled={page === 0} onClick={() => onPage(page - 1)}>‹ 上一页</button>
      <em>第 {page + 1} / {pages} 页</em>
      <button disabled={page >= pages - 1} onClick={() => onPage(page + 1)}>下一页 ›</button>
    </div>
  );
};

/* ---------------- PPT 浏览器（页内大窗，逐页翻看） ---------------- */
const PptViewer: React.FC<{ ppt: PptItem; onClose: () => void }> = ({ ppt, onClose }) => {
  const [slides, setSlides] = useState<PptSlide[] | null>(null);
  const [idx, setIdx] = useState(0);
  const authToken = useAuthStore((s) => s.token) || '';

  useEffect(() => {
    api.get(`/ppt-projects/${ppt.id}/full-state`)
      .then((r) => {
        const st = r.data;
        const arr = st?.data?.slides || st?.slides || [];
        setSlides(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setSlides([]));
  }, [ppt.id]);

  const cur = slides?.[idx];
  const imgSrc = cur?.assetId ? `/api/ppt-assets/${cur.assetId}/view?token=${encodeURIComponent(authToken)}` : null;
  const go = useCallback((d: number) => {
    setIdx((v) => Math.max(0, Math.min((slides?.length || 1) - 1, v + d)));
  }, [slides]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  return (
    <div className="aco24-wrap" onClick={onClose}>
      <div className="aco24-viewer" onClick={(e) => e.stopPropagation()}>
        <header>
          <PlaySquareOutlined className="ic" />
          <b>{ppt.title || 'PPT 预览'}</b>
          {slides && slides.length > 0 && <em>第 {idx + 1} / {slides.length} 页</em>}
          <button className="studio" onClick={() => { window.location.href = `/ppt-designer/${ppt.id}`; }}>
            <RocketOutlined /> 进入 PPT 工作室
          </button>
          <button className="x" onClick={onClose}><CloseOutlined /></button>
        </header>
        <div className="stage">
          <button className="nav l" disabled={idx === 0} onClick={() => go(-1)}><LeftOutlined /></button>
          <div className="canvas">
            {slides === null && <div className="hint"><LoadingOutlined /> 正在装载幻灯片…</div>}
            {slides !== null && slides.length === 0 && <div className="hint">这份 PPT 还没有页面内容</div>}
            {cur && (imgSrc ? (
              <img key={cur.id || idx} src={imgSrc} alt="" draggable={false} />
            ) : (
              <div key={cur.id || idx} className="outline">
                <em>{cur.type || `第 ${cur.no ?? idx + 1} 页`} · 设计稿（尚未出图，进工作室一键生成）</em>
                <b>{cur.title || `幻灯片 ${idx + 1}`}</b>
                {cur.subtitle && <span>{cur.subtitle}</span>}
                {!!cur.bullets?.length && (
                  <ul>{cur.bullets.slice(0, 8).map((x, i) => <li key={i}>{x}</li>)}</ul>
                )}
              </div>
            ))}
          </div>
          <button className="nav r" disabled={!slides || idx >= slides.length - 1} onClick={() => go(1)}><RightOutlined /></button>
        </div>
        {!!slides?.length && (
          <div className="filmstrip">
            {slides.map((s, i) => (
              <button key={s.id || i} className={i === idx ? 'on' : ''} onClick={() => setIdx(i)}>
                <em>{i + 1}</em>
                <span>{(s.title || '').slice(0, 8) || '—'}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------- 文档/PPT 产物详情：原始任务 + 分来源知识记录 ---------------- */
const ArtifactDetail: React.FC<{
  detail: ArtifactDetailState;
  onClose: () => void;
  onDownloadDoc: (task: CollabTask) => void;
  onOpenPpt: (ppt: PptItem) => void;
}> = ({ detail, onClose, onDownloadDoc, onOpenPpt }) => {
  const docItem = detail.kind === 'doc' ? detail.item : null;
  const pptItem = detail.kind === 'ppt' ? detail.item : null;
  const isDoc = Boolean(docItem);
  const title = docItem
    ? (docItem.resultFileName || cleanTaskText(docItem.command) || '文档/表格产物')
    : (pptItem?.title || 'PPT 产物');
  const command = docItem
    ? cleanTaskText(docItem.command)
    : cleanTaskText(pptItem?.requirement || pptItem?.sourceText || pptItem?.title);
  const refs = parseKnowledgeRefs(docItem?.kbDocs || pptItem?.kbDocs);
  const docRefs = refs.filter((ref) => ref.source === 'doc');
  const intelRefs = refs.filter((ref) => ref.source === 'intel');
  const time = docItem?.createdAt || pptItem?.createdAt || pptItem?.updatedAt;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const group = (kind: 'doc' | 'intel', list: KnowledgeRef[]) => (
    <section className={`knowledge-group ${kind}`}>
      <div className="kg-title">
        {kind === 'doc' ? <DatabaseOutlined /> : <GlobalOutlined />}
        <b>{kind === 'doc' ? '文档知识库' : '情报库'}</b>
        <em>{list.length}</em>
      </div>
      {list.length ? (
        <div className="kg-list">
          {list.map((ref, index) => (
            <div className="kg-file" key={`${ref.source}-${ref.name}-${index}`}>
              <i>{kind === 'doc' ? '知识' : '情报'}</i>
              <span title={ref.name}>{ref.name}</span>
              {typeof ref.score === 'number' && <em>{Math.round((ref.score <= 1 ? ref.score * 100 : ref.score))}%</em>}
            </div>
          ))}
        </div>
      ) : <p>本次任务未匹配到{kind === 'doc' ? '文档知识库文件' : '情报库文件'}</p>}
    </section>
  );

  return (
    <div className="aco42-detail" onClick={onClose}>
      <div className="panel" onClick={(event) => event.stopPropagation()}>
        <header>
          {isDoc ? <FileTextOutlined className="ic doc" /> : <PlaySquareOutlined className="ic ppt" />}
          <div>
            <b>{title}</b>
            <span>{isDoc ? '文档/表格产物' : `PPT 产物${pptItem?.slideCount ? ` · ${pptItem.slideCount} 页` : ''}`} · {fmtTime(time)}</span>
          </div>
          <button className="x" onClick={onClose}><CloseOutlined /></button>
        </header>
        <div className="body">
          <section className="task-question">
            <em>用户问题 / 下达任务</em>
            <p>{command || '未记录原始任务内容'}</p>
          </section>
          <div className="knowledge-title">
            <b>本次搜索参考文件</b>
            <span>文档知识库与情报库已分开标记</span>
          </div>
          <div className="knowledge-grid">
            {group('doc', docRefs)}
            {group('intel', intelRefs)}
          </div>
        </div>
        <footer>
          {docItem ? (
            <button onClick={() => onDownloadDoc(docItem)}><DownloadOutlined /> 下载产物</button>
          ) : pptItem ? (
            <>
              <button onClick={() => onOpenPpt(pptItem)}><EyeOutlined /> 查看 PPT</button>
              <button onClick={() => { window.location.href = `/ppt-designer/${pptItem.id}`; }}><RocketOutlined /> PPT 工作室</button>
            </>
          ) : null}
        </footer>
      </div>
    </div>
  );
};

/* ---------------- 产物记录主面板 ---------------- */
const ArtifactVault: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [tab, setTab] = useState<Tab>('image');
  const [images, setImages] = useState<DesignItem[] | null>(null);
  const [imgTotal, setImgTotal] = useState(0);
  const [ppts, setPpts] = useState<PptItem[] | null>(null);
  const [pageImg, setPageImg] = useState(0);
  const [pagePpt, setPagePpt] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const [viewPpt, setViewPpt] = useState<PptItem | null>(null);
  const [viewDetail, setViewDetail] = useState<ArtifactDetailState | null>(null);
  const [agentCategories, setAgentCategories] = useState<AgentArtifactCategory[] | null>(null);
  const [agentItems, setAgentItems] = useState<AgentArtifactRecord[]>([]);
  const [pageAgent, setPageAgent] = useState(0);
  const [viewAgentArtifact, setViewAgentArtifact] = useState<AgentArtifactRecord | null>(null);
  const authToken = useAuthStore((s) => s.token) || '';

  /* 文档：累积式服务端翻页——后台按批拉原始任务，筛出带产物文件的凑满每页，能翻到最早 */
  const [docItems, setDocItems] = useState<CollabTask[] | null>(null);
  const [pageDoc, setPageDoc] = useState(0);
  const [docHasNext, setDocHasNext] = useState(false);
  const docBufRef = useRef<CollabTask[]>([]);   // 累积的带文件任务
  const docSkipRef = useRef(0);                  // 已消费的原始任务数
  const docMoreRef = useRef(true);               // 后台还有更多原始任务

  const loadDocPage = useCallback(async (target: number) => {
    setDocItems(null);
    const need = (target + 1) * DOC_PAGE;
    try {
      while (docBufRef.current.length < need + 1 && docMoreRef.current) {
        // eslint-disable-next-line no-await-in-loop
        const batch = await getRecentTasks(docSkipRef.current, DOC_BATCH);
        docSkipRef.current += batch.length;
        if (batch.length < DOC_BATCH) docMoreRef.current = false;
        docBufRef.current.push(...batch.filter((t) => t.resultFileId));
      }
    } catch { /* 保底用已拿到的 */ }
    const slice = docBufRef.current.slice(target * DOC_PAGE, target * DOC_PAGE + DOC_PAGE);
    setDocItems(slice);
    setDocHasNext(docBufRef.current.length > (target + 1) * DOC_PAGE || docMoreRef.current);
  }, []);

  useEffect(() => { loadDocPage(pageDoc); }, [pageDoc, loadDocPage]);

  /* 设计图：走后台分页接口（每页 10），翻页即拉当前页——图多也不卡 */
  useEffect(() => {
    let alive = true;
    setImages(null);
    api.get('/design-creator', { params: { page: pageImg + 1, pageSize: IMG_PAGE } })
      .then((r) => {
        if (!alive) return;
        const d = r.data || {};
        const list = Array.isArray(d) ? d : d.items || [];
        setImages(list.filter((x: DesignItem) => x.status === 'completed' && (x.assetId || x.resultAssetId)));
        setImgTotal(typeof d.total === 'number' ? d.total : list.length);
      })
      .catch(() => { if (alive) { setImages([]); setImgTotal(0); } });
    return () => { alive = false; };
  }, [pageImg]);

  /* PPT：清单一次拉，前端分页（后台未加分页） */
  useEffect(() => {
    api.get('/ppt-projects')
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data?.items || r.data?.projects || [];
        setPpts(list);
      })
      .catch(() => setPpts([]));
  }, []);

  /* 业务智能体：按当前用户实际加载的智能体动态生成分类，刷新后直接读取持久化产物。 */
  useEffect(() => {
    let alive = true;
    listAgentArtifacts(50)
      .then((data) => {
        if (!alive) return;
        setAgentCategories(data.agents);
        setAgentItems(data.items.filter((item) => item.status === 'completed' && Boolean(item.result)));
      })
      .catch(() => {
        if (!alive) return;
        setAgentCategories([]);
        setAgentItems([]);
      });
    return () => { alive = false; };
  }, []);

  const imgUrl = useCallback(
    (it: DesignItem) => `/api/design-creator/assets/${it.assetId || it.resultAssetId}/view?token=${encodeURIComponent(authToken)}`,
    [authToken],
  );

  const downloadDoc = useCallback(async (t: CollabTask) => {
    try {
      const res = await fetch(getFileDownloadUrl(t.resultFileId!), { headers: { Authorization: `Bearer ${authToken}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = t.resultFileName || '产物文件';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e: any) {
      window.alert(e?.message || '下载失败');
    }
  }, [authToken]);

  /* 设计图=当前页即 images（后台已分页）；文档=累积翻页；PPT=前端 slice */
  const pptPage = useMemo(() => (ppts || []).slice(pagePpt * PAGE, pagePpt * PAGE + PAGE), [ppts, pagePpt]);
  const selectedAgentCode = tab.startsWith('agent:') ? tab.slice('agent:'.length) : '';
  const selectedAgent = agentCategories?.find((agent) => agent.agentCode === selectedAgentCode);
  const selectedAgentItems = useMemo(
    () => agentItems.filter((item) => item.agentCode === selectedAgentCode),
    [agentItems, selectedAgentCode],
  );
  const agentPageItems = useMemo(
    () => selectedAgentItems.slice(pageAgent * AGENT_PAGE, pageAgent * AGENT_PAGE + AGENT_PAGE),
    [selectedAgentItems, pageAgent],
  );

  useEffect(() => { setPageAgent(0); }, [selectedAgentCode]);

  const counts: Partial<Record<Tab, number>> = { image: imgTotal || undefined, ppt: ppts?.length, doc: undefined };
  for (const agent of agentCategories || []) counts[`agent:${agent.agentCode}`] = agent.artifactCount;
  const tabs: Array<[Tab, string]> = [
    ['image', '🎨 设计图'],
    ['ppt', '📽 PPT'],
    ['doc', '📄 文档表格'],
    ...(agentCategories || []).map((agent): [Tab, string] => [`agent:${agent.agentCode}`, `🤖 ${agent.name}`]),
  ];

  return (
    <div className="aco23-wrap" onClick={onClose}>
      <div className="aco23-panel" onClick={(e) => e.stopPropagation()}>
        <header>
          <PictureOutlined className="ic" />
          <div className="tt">
            <b>产物记录</b>
            <span>历史生成的设计图 · PPT · 文档，随时翻出来用</span>
          </div>
          <div className="tabs">
            {tabs.map(([k, label]) => (
              <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
                {label}{typeof counts[k] === 'number' ? ` (${counts[k]})` : ''}
              </button>
            ))}
            {agentCategories === null && <button disabled><LoadingOutlined /> 智能体产物</button>}
          </div>
          <button className="x" onClick={onClose}><CloseOutlined /></button>
        </header>

        <div className="body">
          {tab === 'image' && (
            <>
              <div className="imggrid">
                {images === null && <div className="empty"><LoadingOutlined /> 正在调取设计图…</div>}
                {images?.length === 0 && imgTotal === 0 && <div className="empty">还没有生成过设计图</div>}
                {(images || []).map((it) => (
                  <button key={it.id} className="imgcard" onClick={() => setPreview(imgUrl(it))}>
                    <img src={imgUrl(it)} alt="" loading="lazy" draggable={false} />
                    <span className="cap">
                      <b>{(it.prompt || '设计图').slice(0, 26)}</b>
                      <em>{fmtTime(it.createdAt)}</em>
                    </span>
                  </button>
                ))}
              </div>
              <Pager page={pageImg} total={imgTotal} size={IMG_PAGE} onPage={setPageImg} />
            </>
          )}

          {tab === 'ppt' && (
            <>
              <div className="rowlist">
                {ppts === null && <div className="empty"><LoadingOutlined /> 正在调取 PPT…</div>}
                {ppts?.length === 0 && <div className="empty">还没有生成过 PPT</div>}
                {pptPage.map((p) => (
                  <div key={p.id} className="row">
                    <PlaySquareOutlined className="fi ppt" />
                    <div className="artifact-main">
                      <b>{p.title || '未命名 PPT'}</b>
                      <span title={cleanTaskText(p.requirement || p.sourceText || p.title)}>
                        任务：{taskPreview(p.requirement || p.sourceText || p.title) || '未记录原始任务'}
                      </span>
                    </div>
                    <em>{fmtTime(p.createdAt || p.updatedAt)}</em>
                    <button className="act" onClick={() => setViewDetail({ kind: 'ppt', item: p })}>
                      <FileTextOutlined /> 查看详情
                    </button>
                    <button className="act main" onClick={() => setViewPpt(p)}>
                      <EyeOutlined /> 查看 PPT
                    </button>
                  </div>
                ))}
              </div>
              <Pager page={pagePpt} total={ppts?.length || 0} onPage={setPagePpt} />
            </>
          )}

          {tab === 'doc' && (
            <>
              <div className="rowlist">
                {docItems === null && <div className="empty"><LoadingOutlined /> 正在调取文档…</div>}
                {docItems?.length === 0 && pageDoc === 0 && <div className="empty">还没有生成过文档/表格产物</div>}
                {(docItems || []).map((t) => (
                  <div key={t.id} className="row">
                    <FileTextOutlined className="fi doc" />
                    <div className="artifact-main">
                      <b>{t.resultFileName || taskPreview(t.command) || '文档/表格产物'}</b>
                      <span title={cleanTaskText(t.command)}>任务：{taskPreview(t.command) || '未记录原始任务'}</span>
                    </div>
                    <em>{fmtTime(t.createdAt)}</em>
                    <button className="act" onClick={() => setViewDetail({ kind: 'doc', item: t })}>
                      <EyeOutlined /> 查看详情
                    </button>
                    <button className="act" onClick={() => downloadDoc(t)}>
                      <DownloadOutlined /> 下载
                    </button>
                  </div>
                ))}
              </div>
              {(pageDoc > 0 || docHasNext) && docItems !== null && (
                <div className="pager">
                  <button disabled={pageDoc === 0} onClick={() => setPageDoc((p) => Math.max(0, p - 1))}>‹ 上一页</button>
                  <em>第 {pageDoc + 1} 页</em>
                  <button disabled={!docHasNext} onClick={() => setPageDoc((p) => p + 1)}>下一页 ›</button>
                </div>
              )}
            </>
          )}

          {selectedAgentCode && (
            <>
              <div className="agent-category-note">
                <RobotOutlined />
                <span><b>{selectedAgent?.name || '智能体产物'}</b>{selectedAgent?.description ? ` · ${selectedAgent.description}` : ''}</span>
              </div>
              <div className="rowlist">
                {agentCategories === null && <div className="empty"><LoadingOutlined /> 正在调取智能体产物…</div>}
                {agentCategories !== null && selectedAgentItems.length === 0 && <div className="empty">这个智能体还没有已完成的产物</div>}
                {agentPageItems.map((item) => (
                  <div key={item.id} className="row agent-row">
                    <RobotOutlined className="fi agent" />
                    <div className="agent-product-title">
                      <b>{item.title || item.command || '智能体任务结果'}</b>
                      <span>{item.command}</span>
                    </div>
                    <i className="kind">{item.resultKind === 'html' ? 'HTML 报告' : item.resultKind === 'markdown' ? 'Markdown 报告' : '口头汇报'}</i>
                    <em>{fmtTime(item.finishedAt || item.startedAt)}</em>
                    <button className="act main" onClick={() => setViewAgentArtifact(item)}>
                      <EyeOutlined /> 查看产物
                    </button>
                  </div>
                ))}
              </div>
              <Pager page={pageAgent} total={selectedAgentItems.length} size={AGENT_PAGE} onPage={setPageAgent} />
            </>
          )}
        </div>
      </div>

      {/* 大图预览 */}
      {preview && (
        <div className="aco23-preview" onClick={(e) => { e.stopPropagation(); setPreview(null); }}>
          <img src={preview} alt="" />
          <a className="dl" href={preview} download target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
            <DownloadOutlined /> 下载原图
          </a>
        </div>
      )}

      {/* PPT 浏览器 */}
      {viewPpt && <PptViewer ppt={viewPpt} onClose={() => setViewPpt(null)} />}

      {/* 文档/PPT 详情：展示原始任务与两类知识来源 */}
      {viewDetail && (
        <ArtifactDetail
          detail={viewDetail}
          onClose={() => setViewDetail(null)}
          onDownloadDoc={(task) => void downloadDoc(task)}
          onOpenPpt={(ppt) => { setViewDetail(null); setViewPpt(ppt); }}
        />
      )}

      {/* 业务智能体持久化产物预览 */}
      {viewAgentArtifact && (
        <div className="aco41-agent-preview" onClick={(e) => { e.stopPropagation(); setViewAgentArtifact(null); }}>
          <section onClick={(e) => e.stopPropagation()}>
            <header>
              <RobotOutlined className="ic" />
              <div>
                <b>{viewAgentArtifact.title || '智能体任务结果'}</b>
                <span>{viewAgentArtifact.agentName} · {fmtTime(viewAgentArtifact.finishedAt || viewAgentArtifact.startedAt)}</span>
              </div>
              <button className="x" onClick={() => setViewAgentArtifact(null)}><CloseOutlined /></button>
            </header>
            <div className="command"><em>任务指令</em><span>{viewAgentArtifact.command}</span></div>
            <div className="content">
              {viewAgentArtifact.resultKind === 'html' ? (
                <iframe title={viewAgentArtifact.title || '智能体产物'} srcDoc={viewAgentArtifact.result} sandbox="allow-same-origin" />
              ) : (
                <article><ReactMarkdown remarkPlugins={[remarkGfm]}>{viewAgentArtifact.result}</ReactMarkdown></article>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default ArtifactVault;
